import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ImageStats, Lab, Scan, ScanStatus } from "../../../model";
import { euclideanDeltaC } from "src/utils/color-tools";
import { UserRole } from "src/model";
import { fallbackImage } from "../index";
import { BackendService } from "../../../services/backend";
import { AuthenticationState } from "../../../store/auth/auth.state";
import { TrainingState } from "../../../store/training/training.state";
import { ModelState } from "../../../store/model/model.state";
import { ProductionBatchState } from "../../../store/batch/batch.state";
import { ScannerState } from "../../../store/scanner/scanner.state";
import { OperatorReasonDialogComponent } from '../operator-reason-dialog/operator-reason-dialog.component';
import { OPERATOR_REASONS, OperatorReason, reasonLabel } from '../../../model/operator-reason';
import { finalize } from 'rxjs';
import { ImagePixelSize } from '../inspection-overlay/inspection-overlay.component';

const PREFS_KEY = 'scan-dialog-prefs';
const DEFAULT_WIDTH = '25vw';

interface DialogPrefs { x: number; y: number; width: string; }

/**
 * Temporary check on the "Measured by Spectrophotometer" fields: an L at or below this looks like a difference from
 * the expected colour, not the absolute reading (the lab entered differences until 2026-10-30). a and b are not
 * checked. It only warns; saving is still allowed after a confirm.
 */
const SPECTRO_DELTA_MAX_L = 10;

/** How many of L, a and b hold a value. A typed 0 counts; an empty number input is null (or '' while typing). */
function filledChannels(reading: Lab | null | undefined): number {
  if (!reading) return 0;
  return [reading.l, reading.a, reading.b].filter(v => v != null && (v as unknown) !== '').length;
}

/** One or two of L, a and b: not a reading, and not "no reading" either. */
function isPartialReading(reading: Lab | null | undefined): boolean {
  const filled = filledChannels(reading);
  return filled > 0 && filled < 3;
}

/** A copy of the scan whose reading the inputs can bind to: all three null when the scan has no reading. */
function withEditableSpectro(scan: Scan): Scan {
  const reading = scan.measuredBySpectro
    ? { ...scan.measuredBySpectro }
    : { l: null, a: null, b: null } as unknown as Lab;
  return { ...scan, measuredBySpectro: reading };
}

@Component({
  selector: 'app-scan-dialog',
  templateUrl: './scan-dialog.component.html',
  styleUrls: ['./scan-dialog.component.scss'],
  standalone: false
})
export class ScanDialogComponent implements OnChanges {
  readonly trainingState = inject(TrainingState);
  readonly modelState = inject(ModelState);
  private readonly scannerState = inject(ScannerState);
  private readonly modal = inject(NzModalService);
  readonly noDescriptionText = $localize`No description provided`;
  // Expose UserRole for template-side role checks (admin-only Delete button).
  readonly UserRole = UserRole;

  /** Pending status change awaiting operator reason selection. */
  private _pendingStatus: ScanStatus | null = null;

  /** True while the reason popup was opened by the default action (Eject / Continue), not a status change. */
  private _pendingDefaultAction = false;

  /** True while defaultAction() is asking whether a reason is needed. */
  private _checkingDefaultAction = false;

  /** Set when Save was pressed with a partial spectrophotometer reading (see showSpectroError). */
  spectroIncompleteOnSave = false;

  private prefs: DialogPrefs | null = (() => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) ?? 'null'); } catch { return null; }
  })();

  get nzWidth(): string { return this.prefs?.width ?? DEFAULT_WIDTH; }

  /**
   * Pure guests keep the classic fully-blocking modal (backdrop swallows clicks, and the
   * dblclick-on-backdrop shortcut in onAfterOpen closes the dialog). Every other role gets the
   * non-blocking variant — the backdrop and wrap go click-through so the page stays usable while
   * the dialog floats on top, which necessarily disables that dblclick shortcut.
   */
  get isGuestOnly(): boolean {
    return this.authState.hasExactlyRole(UserRole.Guest);
  }

  get nzStyle(): object {
    if (this.prefs) {
      return { marginInlineEnd: '5px', top: `${this.prefs.y}px`, left: `${this.prefs.x}px`, position: 'fixed' };
    }
    return { marginInlineEnd: '5px', top: '5px' };
  }

  onAfterOpen(): void {
    // The drag handle is the modal header; listen for mouseup on the whole document
    // and read the transform the nzDraggable directive applied to .ant-modal-wrap or .ant-modal.
    const dragHandler = () => {
      const container = document.querySelector('.ant-modal') as HTMLElement | null;
      if (!container) return;
      const transform = container.style.transform;
      const match = transform.match(/translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/);
      if (match) {
        const dx = parseFloat(match[1]);
        const dy = parseFloat(match[2]);
        const rect = container.getBoundingClientRect();
        this.savePrefs({
          x: rect.left - dx + dx,   // absolute left = rect.left
          y: rect.top  - dy + dy,   // absolute top  = rect.top
          width: container.style.width || this.nzWidth,
        });
      }
    };
    document.addEventListener('mouseup', dragHandler);

    // Double-click on the backdrop (wrap but NOT the modal itself) closes the dialog.
    const wrap = document.querySelector('.ant-modal-wrap') as HTMLElement | null;
    const dblClickHandler = (e: MouseEvent) => {
      if (e.target === wrap) this.cancel();
    };
    wrap?.addEventListener('dblclick', dblClickHandler);

    this._dragCleanup = () => {
      document.removeEventListener('mouseup', dragHandler);
      wrap?.removeEventListener('dblclick', dblClickHandler);
    };
  }

  private _dragCleanup?: () => void;

  /** Natural size of the scan image once it has loaded, with the URL it belongs to. */
  private loadedScanImage: { url: string; size: ImagePixelSize } | null = null;

  /** Natural size of the current scan's image, or null until it has loaded (the overlay draws nothing meanwhile). */
  get overlayImageSize(): ImagePixelSize | null {
    const url = this.scan?.imageUrl;
    return url && this.loadedScanImage?.url === url ? this.loadedScanImage.size : null;
  }

  onScanImageLoad(event: Event): void {
    const image = event.target as HTMLImageElement;
    const url = this.scan?.imageUrl;
    // The fallback image also fires load (when the real one failed); its size means nothing for the overlay.
    if (!url || !image.naturalWidth || image.currentSrc !== new URL(url, document.baseURI).href) return;
    this.loadedScanImage = { url, size: { width: image.naturalWidth, height: image.naturalHeight } };
  }

  onAfterClose(): void {
    this._dragCleanup?.();
    this._dragCleanup = undefined;
  }

  private savePrefs(prefs: DialogPrefs): void {
    this.prefs = prefs;
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }

  get inspectionModel() {
    if (!this.scan?.inspectionModelId) return undefined;
    return this.modelState.all.find(m => m.id === this.scan!.inspectionModelId);
  }

  @Input()
  scan: Scan | null | undefined;

  @Input()
  title: string | undefined;

  @Input()
  defaultActionTitle = 'Close';

  @Input()
  closeOnDefaultAction = true;

  @Input()
  extraActionTitle?: string = undefined;

  @Input()
  selectedTabIndex = 0;

  /**
   * True when this dialog is the scanner's live Deciding prompt. The Operator's decision must then
   * go through the scanner state machine (which releases or ejects the bucket), not a plain scan update.
   */
  @Input()
  liveDecision = false;

  /**
   * True on the live scanner dialogs (Deciding: Eject, Ejected: Continue), where the default action moves
   * the line on and keeps the algorithm's status. An Operator is then asked for a reason first (DEV-282),
   * unless this bucket already has one; the reason goes out with onAction.
   */
  @Input()
  defaultActionNeedsReason = false;

  @Output()
  selectedTabIndexChange = new EventEmitter<number>();

  @Output()
  onClose = new EventEmitter<string>();

  /** Default action pressed. Carries the Operator's reason when one was asked for, otherwise null. */
  @Output()
  onAction = new EventEmitter<string | null>();


  @Output()
  onForward = new EventEmitter();

  @Output()
  onBackward = new EventEmitter();

  @Output()
  onExtraAction = new EventEmitter();

  @Output()
  onStatusUpdate = new EventEmitter<ScanStatus>();

  @Output()
  onDelete = new EventEmitter<Scan>();

  /** Reference to the reason-picker popup used for Operator status changes. */
  @ViewChild(OperatorReasonDialogComponent)
  private reasonDialog?: OperatorReasonDialogComponent;

  /** The spectro L input. */
  @ViewChild('spectroL', { read: ElementRef })
  private spectroLInput?: ElementRef<HTMLInputElement>;

  /** The user has left the spectro L field at least once — a difference-like L is only warned about after that. */
  spectroLLeft = false;

  readonly spectroDeltaMaxL = SPECTRO_DELTA_MAX_L;
  readonly spectroKeys: readonly (keyof Lab)[] = ['l', 'a', 'b'];

  fallbackImage = fallbackImage;
  euclideanDeltaC = euclideanDeltaC;
  /** Resolves a stored reason key to its localized label; null → render nothing (AC-5). */
  readonly reasonLabel = reasonLabel;
  /** AC-4: Full reason list for the Admin/Lab Details-tab dropdown. */
  readonly reasonOptions: OperatorReason[] = OPERATOR_REASONS;
  public stats: ImageStats | undefined;

  /** The localized label for the currently displayed reason key (null = no reason / render nothing). */
  get currentReasonLabel(): string | null {
    return reasonLabel(this.scan?.operatorStatusChangeReason);
  }

  constructor(
    public authState: AuthenticationState,
    private backend: BackendService,
    private batchState: ProductionBatchState,
    private cdr: ChangeDetectorRef
  ) {
  }

  colorPicker(color: Lab): string {
    return `lab(${color?.l} ${color?.a} ${color?.b})`;
  }

  get _title() {
    return this.title ?? `Scan ${this.scan?.sequenceInBatch} - ${this.scan?.inspectedTime ?? this.scan?.scannedTime ?? ''} - dE ${this.scan?.dE}`
  }

  cancel() {
    this.scan = null;
    this.onClose.emit('cancel');
  }

  forward() {
    this.onForward.emit();
  }

  backward() {
    this.onBackward.emit();
  }

  /**
   * Default action button. With defaultActionNeedsReason, asks the scanner whether an Operator reason is
   * needed first; if so, opens the reason popup and waits (the dialog stays open, so Cancel leaves it as it was).
   */
  defaultAction() {
    if (!this.defaultActionNeedsReason) {
      this.completeDefaultAction(null);
      return;
    }
    if (this._checkingDefaultAction) return; // double click while the check is in flight
    this._checkingDefaultAction = true;
    this.scannerState.continueNeedsOperatorReason()
      .pipe(finalize(() => this._checkingDefaultAction = false))
      .subscribe(needsReason => {
        if (needsReason) {
          this._pendingDefaultAction = true;
          this.reasonDialog?.open();
        } else {
          this.completeDefaultAction(null);
        }
      });
  }

  private completeDefaultAction(reason: string | null) {
    if (this.closeOnDefaultAction)
      this.scan = null;
    this.onAction.emit(reason);
  }

  extraAction() {
    this.onExtraAction.emit();
  }

  mirrorArrow() {
    const arrow = document.getElementsByClassName('nav-arrow')[0];
    if (arrow && window.getComputedStyle(arrow, null).getPropertyValue('direction') == "rtl") return "transform:  scaleX(-1)";
    return undefined;
  }

  get canEditTrainingData(): boolean {
    return this.authState.hasAnyRole(UserRole.Admin, UserRole.Laboratory);
  }

  /** True when the current user can see/edit the reason dropdown in the Details tab (Admin & Lab only). */
  get canEditReason(): boolean {
    return this.authState.hasAnyRole(UserRole.Admin, UserRole.Laboratory);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['scan'] && changes['scan'].currentValue) {
      // Clone scan to avoid modifying readonly state directly
      this.scan = withEditableSpectro(changes['scan'].currentValue);
      this.spectroIncompleteOnSave = false;
      this.spectroLLeft = false;
    }
  }

  /**
   * Handles radio-group ngModelChange (AC-2).
   * - Operator: intercepts the change, stores the pending status, opens the reason popup.
   *   The radio auto-reverts on next CD because [ngModel] is one-way bound to `scan.state`.
   * - Non-Operator: emits onStatusUpdate as before (parent handles the dispatch).
   */
  onRadioStatusChange(newStatus: ScanStatus): void {
    if (this.authState.hasExactlyRole(UserRole.Operator)) {
      this._pendingStatus = newStatus;
      this.reasonDialog?.open();
    } else {
      this.onStatusUpdate.emit(newStatus);
    }
  }

  /**
   * Called when the Operator confirms a reason in the popup (AC-2 + AC-3).
   * - Default action (Eject / Continue, DEV-282): completes it with the reason; the status is not touched.
   * - Live Deciding dialog: sends the decision with the reason to the scanner, which saves it and
   *   releases the bucket (Passed/Ignored). The dialog closes when the scanner leaves Deciding.
   * - Otherwise: dispatches updateStatus with the reason key, then updates the local scan clone
   *   so the role tag and reason label refresh without waiting for SSE.
   */
  onReasonSelected(reasonKey: string): void {
    if (this._pendingDefaultAction) {
      this._pendingDefaultAction = false;
      this.completeDefaultAction(reasonKey);
      return;
    }
    if (!this.scan || !this._pendingStatus) return;
    const status = this._pendingStatus;
    this._pendingStatus = null;
    if (this.liveDecision) {
      // Fire-and-forget: nothing here needs the result. A second manual-inspection POST after the scanner has left
      // Deciding puts the scanner into Failed; subscribing used to send it twice, fixed by singleRequest() (DEV-314).
      this.scannerState.setManualInspectionResult(status, reasonKey);
      return;
    }
    // Dispatch directly — keeps parent @Output bindings unchanged.
    this.batchState.updateStatus(this.scan, status, reasonKey).subscribe({
      next: (batch) => {
        // Update local scan clone with the new state so the UI refreshes immediately.
        const updated = batch.scans?.find(s => s.id === this.scan?.id);
        if (updated) {
          this.scan = withEditableSpectro({ ...updated, productionBatch: this.scan!.productionBatch });
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('[scan-dialog] onReasonSelected: updateStatus failed', err)
    });
  }

  /**
   * Called when the Operator cancels the reason popup (AC-2).
   * Clears the pending status — the radio auto-reverts because [ngModel] is one-way.
   * A pending default action (Eject / Continue) is dropped too: the line stays stopped (DEV-282).
   */
  onReasonCancelled(): void {
    this._pendingStatus = null;
    this._pendingDefaultAction = false;
    // Force change detection so the radio re-reads scan.state and snaps back.
    this.cdr.markForCheck();
  }

  /** The batch's expected (Reference) Lab; null when the batch has none, or it is 0/0/0 (no colour). */
  get spectroExpected(): Lab | null {
    const lab = this.scan?.productionBatch?.color?.lab;
    return !lab || (lab.l === 0 && lab.a === 0 && lab.b === 0) ? null : lab;
  }

  /** Measured − expected for one channel; null when either side is missing (an empty input is null, or '' while typing). */
  spectroDelta(key: keyof Lab): number | null {
    const measured = this.scan?.measuredBySpectro?.[key];
    const expected = this.spectroExpected?.[key];
    return measured == null || (measured as unknown) === '' || expected == null ? null : measured - expected;
  }

  /** True when L is SPECTRO_DELTA_MAX_L or less (empty is fine); with onlyLeft, only once the L field has been left. */
  spectroLooksLikeDelta(onlyLeft = false): boolean {
    const l = this.scan?.measuredBySpectro?.l;
    // An empty number input is null, or '' while typing — and '' <= 10 would be true.
    return l != null && (l as unknown) !== '' && l <= SPECTRO_DELTA_MAX_L && (!onlyLeft || this.spectroLLeft);
  }

  /** True when Save was pressed with only some of L, a and b filled in, until the reading is complete or empty. */
  get showSpectroError(): boolean {
    return this.spectroIncompleteOnSave && isPartialReading(this.scan?.measuredBySpectro);
  }

  /**
   * Saves the Details tab. The spectrophotometer reading is all three of L, a and b or nothing (DEV-302):
   * a partial reading is not sent at all. A difference-like L first asks "are you sure" (default No — then
   * the L field gets focus).
   */
  save() {
    if (!this.scan) return;
    if (isPartialReading(this.scan.measuredBySpectro)) {
      this.spectroIncompleteOnSave = true;
      return;
    }
    this.spectroIncompleteOnSave = false;
    if (!this.spectroLooksLikeDelta()) {
      this.saveScan();
      return;
    }
    this.spectroLLeft = true;
    const value = this.scan.measuredBySpectro!.l;
    const confirm = this.modal.confirm({
      nzTitle: $localize`:@@spectroConfirmTitle:Strange spectrophotometer values`,
      nzContent: $localize`:@@spectroConfirmDelta:L = ${value}:value: looks like a difference, not an absolute reading. Are you sure you want to save it?`,
      nzOkText: $localize`:@@spectroConfirmYes:Yes, save`,
      nzOkType: 'default',
      nzOkDanger: true,
      nzCancelText: $localize`:@@spectroConfirmNo:No`,
      nzAutofocus: 'cancel',
      nzOnOk: () => { this.saveScan(); return true; },
    });
    // afterClose comes after the confirm has handed focus back to Save. No, Esc and the mask close it without a result.
    confirm.afterClose.subscribe(saved => {
      if (!saved) this.spectroLInput?.nativeElement.focus();
    });
  }

  /** Sends the Details tab; empty inputs go out as `measuredBySpectro: null` (DEV-302). */
  private saveScan() {
    if (this.scan) {
      const reading = this.scan.measuredBySpectro;
      const scanToSave = { ...this.scan, measuredBySpectro: filledChannels(reading) === 0 ? null : reading };
      // Remove circular reference
      delete scanToSave.productionBatch;
      this.backend.updateScan(scanToSave).subscribe(updatedScan => {
        this.batchState.updateScanDetails(updatedScan);
        this.scan = withEditableSpectro({ ...updatedScan, productionBatch: this.scan!.productionBatch });
      });
    }
  }

  /**
   * Admin-only destructive action — wired to a popconfirm in the template.
   * Calls DELETE /api/scans/{id}, removes the scan from the local batch
   * cache, emits onDelete, and closes the dialog.
   * Logs each step at debug level so an operator can correlate with the
   * server-side ScansService.DeleteScan trace.
   */
  deleteScan() {
    if (!this.scan) {
      console.debug('[scan-dialog] deleteScan: no scan loaded; ignoring');
      return;
    }
    const scanToDelete = this.scan;
    console.debug('[scan-dialog] deleteScan: requesting delete of scan',
      scanToDelete.id, 'from batch', scanToDelete.productionBatchId);
    this.backend.deleteScan(scanToDelete.id).subscribe({
      next: () => {
        console.debug('[scan-dialog] deleteScan: server confirmed delete of scan', scanToDelete.id);
        this.batchState.removeScanFromBatch(scanToDelete);
        this.onDelete.emit(scanToDelete);
        this.scan = null;
        this.onClose.emit('deleted');
      },
      error: err => {
        console.error('[scan-dialog] deleteScan: server returned error for scan', scanToDelete.id, err);
      }
    });
  }
}
